"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts"
import type { PieLabelRenderProps } from "recharts"
import { Percent, Loader2, AlertCircle, Download, Settings2, TrendingUp, Package, BarChart3, Info } from "lucide-react"
import { format } from "date-fns"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription } from "@/components/ui/alert"
import * as XLSX from "xlsx"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import {
  AttributionDateRange,
  ChannelStat,
  DeleteConfirmationState,
  EditingExpenseState,
  InfluencerRecord,
  InfluencerActionPrompt,
  MetadataBadgeContext,
  MetadataTableRow,
  SourceDetail,
} from "./types"
import { buildOrdersPageHref, formatCurrency } from "./utils"
import { useAttributionStats, useInfluencerData } from "./use-attribution-data"
import { useExpenseManagement } from "./use-expense-management"
import { useComparisonData } from "./use-comparison-data"
import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { RevenueOrdersKpis } from "./revenue-orders-kpis"
import { InfluencerDialog } from "./dialogs/influencer-dialog"
import { ChannelPerformanceTable } from "./channel-table"
import { MetadataDialog, type MetadataSortConfig } from "./dialogs/metadata-dialog"
import {
  dayRangeToUtcRange,
  isSingleMonthRange,
} from "@/lib/date-utils"
import { DEFAULT_ATTRIBUTION_MODEL } from "@/lib/attribution/attribution-models"
import { AttributionModelSelect } from "./attribution-model-select"
import { useAttributionModelPreferenceQuery } from "@/hooks/queries/attribution-model-preference"
import { useSetAttributionModelPreferenceMutation } from "@/hooks/mutations/attribution-model-preference"
import { MinOrderTotalEditor } from "./min-order-total-editor"
import { PeriodField, StoreSelect } from "./report-scope-controls"
import { DEFAULT_COMPARISON_MODE, type ShopifyPeriod, type ComparisonMode } from "./period-constants"
import { ComparisonSelector } from "./comparison-selector"
import { resolveComparisonRange } from "./get-comparison-range"
import { useProductDailySeries } from "./use-product-daily-series"
import { ProductSalesChart } from "./product-sales-chart"
import { useTranslations } from "next-intl"




const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
]

interface AttributionDashboardProps {
  integrationId: string
  userId: string
  dateRange: AttributionDateRange
  period: ShopifyPeriod
  customRange?: { from: string; to: string } | null
  onPeriodChange: (period: ShopifyPeriod, customRange?: { from: string; to: string }) => void
  comparisonMode?: ComparisonMode
  comparisonCustomRange?: { from: string; to: string } | null
  onComparisonChange?: (mode: ComparisonMode, customRange?: { from: string; to: string }) => void
  configRevision?: number
  minimumOrderTotal: number
  onMinOrderSave: (value: number) => Promise<void>
  isSettingsLoading: boolean
  canManage: boolean
  onManageChannels: () => void
  productIds?: number[]
  stores: { id: string; shop_domain: string }[]
  selectedStore: string
  onStoreChange: (storeId: string) => void
  onProductFilterToggle: () => void
  productFilterCount: number
  productFilterOpen: boolean
}

export function AttributionDashboard({
  integrationId,
  userId,
  dateRange,
  period,
  customRange = null,
  onPeriodChange,
  comparisonMode = DEFAULT_COMPARISON_MODE,
  comparisonCustomRange = null,
  onComparisonChange,
  configRevision = 0,
  minimumOrderTotal,
  onMinOrderSave,
  isSettingsLoading,
  canManage,
  onManageChannels,
  productIds = [],
  stores,
  selectedStore,
  onStoreChange,
  onProductFilterToggle,
  productFilterCount,
  productFilterOpen,
}: AttributionDashboardProps) {


  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardAttributionDashboard")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth()
  const attributionModel = useAttributionModelPreferenceQuery().data?.model ?? DEFAULT_ATTRIBUTION_MODEL
  const { mutate: saveAttributionModel } = useSetAttributionModelPreferenceMutation()
  const [sortBy, setSortBy] = useState<"channel" | "orders" | "revenue" | "aov" | "roi">("revenue")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")

  useEffect(() => {
    setMetadataDialogOpen(false)
    setActiveMetadataContext(null)
    setMetadataRows([])
    setMetadataTotals({ orders: 0, revenue: 0 })
    setMetadataCurrency(null)
  }, [attributionModel, minimumOrderTotal])

  const { influencers, refreshInfluencers } = useInfluencerData({
    integrationId,
    configRevision,
  })
  const { stats, expenses, isLoading, refresh: refreshStats } = useAttributionStats({
    integrationId,
    dateRange,
    configRevision,
    influencers,
    attributionModel,
    minimumOrderTotal,
    productIds,
  })
  const {
    expenseEditingRules,
    canEditChannelExpense,
    canEditSourceExpense,
    canDeleteExpense,
    shouldShowDelete,
    getExpenseId,
    getEffectiveSourceExpense,
    getExpensePeriodForMonth,
  } = useExpenseManagement({
    expenses,
    dateRange,
  })

  const comparisonEnabled = comparisonMode !== "none"

  const comparisonCustomDateRange = useMemo<AttributionDateRange | null>(() => {
    if (comparisonMode !== "custom" || !comparisonCustomRange) return null
    const { month_start_utc, month_end_utc } = dayRangeToUtcRange(
      comparisonCustomRange.from,
      comparisonCustomRange.to,
      dateRange.timezone,
    )
    return { month_start_utc, month_end_utc, timezone: dateRange.timezone }
  }, [comparisonMode, comparisonCustomRange, dateRange.timezone])

  const comparisonDateRange = useMemo(
    () => resolveComparisonRange(comparisonMode, dateRange, comparisonCustomDateRange),
    [comparisonMode, dateRange, comparisonCustomDateRange],
  )

  const { previous, deltas, dailySeries } = useComparisonData({
    integrationId,
    dateRange,
    comparisonDateRange,
    comparisonEnabled,
    configRevision,
    attributionModel,
    minimumOrderTotal,
    productIds,
  })

  const { series: productDailySeries, isLoading: productDailyLoading } = useProductDailySeries({
    integrationId,
    dateRange,
    productIds,
    minimumOrderTotal,
  })

  const [activeTab, setActiveTab] = useState("table")

  // Fall back to Table View when the Product Sales tab is no longer available
  useEffect(() => {
    if (activeTab === "product-sales" && productIds.length === 0) {
      setActiveTab("table")
    }
  }, [activeTab, productIds.length])

  const [editingExpense, setEditingExpense] = useState<EditingExpenseState | null>(null)
  const [expenseInputs, setExpenseInputs] = useState<{ [key: string]: number }>({})
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState | null>(null)

  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set())
  const [expandedInfluencers, setExpandedInfluencers] = useState<Set<string>>(new Set())
  const [sourceFilters, setSourceFilters] = useState<{ [channel: string]: string }>({})

  const [influencerDialogOpen, setInfluencerDialogOpen] = useState(false)
  const [influencerDialogMode, setInfluencerDialogMode] = useState<"create" | "add-code">("create")
  const [targetInfluencer, setTargetInfluencer] = useState<InfluencerRecord | null>(null)
  const [newInfluencerCode, setNewInfluencerCode] = useState("")
  const [newInfluencerName, setNewInfluencerName] = useState("")
  const [isAddingInfluencer, setIsAddingInfluencer] = useState(false)
  const [influencerErrors, setInfluencerErrors] = useState<{
    general?: string
    code?: string
    name?: string
  }>({})
  const { toast } = useToast()

  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false)
  const [activeMetadataContext, setActiveMetadataContext] = useState<MetadataBadgeContext | null>(null)
  const [metadataRows, setMetadataRows] = useState<MetadataTableRow[]>([])
  const [metadataTotals, setMetadataTotals] = useState<{ orders: number; revenue: number }>({ orders: 0, revenue: 0 })
  const [metadataCurrency, setMetadataCurrency] = useState<string | null>(null)
  const [isMetadataLoading, setIsMetadataLoading] = useState(false)

  const resetInfluencerDialogState = () => {
    setInfluencerDialogMode("create")
    setTargetInfluencer(null)
    setNewInfluencerCode("")
    setNewInfluencerName("")
    setInfluencerErrors({})
  }

  const handleInfluencerDialogOpenChange = (open: boolean) => {
    setInfluencerDialogOpen(open)
    if (!open) {
      resetInfluencerDialogState()
    }
  }

  const openCreateInfluencerDialog = () => {
    resetInfluencerDialogState()
    setInfluencerDialogMode("create")
    setInfluencerDialogOpen(true)
  }

  const openAddCodeDialog = (influencer: InfluencerRecord) => {
    setInfluencerDialogMode("add-code")
    setTargetInfluencer(influencer)
    setNewInfluencerCode("")
    setInfluencerErrors({})
    setInfluencerDialogOpen(true)
  }

  const handleInfluencerCodeChange = (value: string) => {
    setNewInfluencerCode(value)
    if (influencerErrors.code) {
      setInfluencerErrors((prev) => ({ ...prev, code: undefined }))
    }
  }

  const handleInfluencerNameChange = (value: string) => {
    setNewInfluencerName(value)
    if (influencerErrors.name) {
      setInfluencerErrors((prev) => ({ ...prev, name: undefined }))
    }
  }

  const metadataSubtitle = useMemo(() => {
    if (!activeMetadataContext) {
      return ""
    }

    if (activeMetadataContext.scope === "source") {
      return activeMetadataContext.sourceLabel || activeMetadataContext.sourceName || ""
    }

    if (activeMetadataContext.scope === "influencer") {
      return (
        activeMetadataContext.influencerName ||
        activeMetadataContext.sourceLabel ||
        activeMetadataContext.sourceName ||
        ""
      )
    }

    if (activeMetadataContext.scope === "code") {
      const parts: string[] = []
      if (activeMetadataContext.influencerName) {
        parts.push(activeMetadataContext.influencerName)
      }
      if (activeMetadataContext.discountCode) {
        parts.push(activeMetadataContext.discountCode)
      } else if (activeMetadataContext.sourceLabel ?? activeMetadataContext.sourceName) {
        parts.push((activeMetadataContext.sourceLabel ?? activeMetadataContext.sourceName)!)
      }
      return parts.join(" → ")
    }

    return ""
  }, [activeMetadataContext])

  const metadataBadgeLabel = useMemo(() => {
    if (!activeMetadataContext) {
      return ""
    }
    return activeMetadataContext.badgeType === "discount" ? "Discount" : "UTM"
  }, [activeMetadataContext])

  const linkedOrderCount = useMemo(() => {
    if (!activeMetadataContext?.orderIds) {
      return 0
    }
    const uniqueIds = activeMetadataContext.orderIds.filter(Boolean)
    return new Set(uniqueIds).size
  }, [activeMetadataContext])

  const handleMetadataDialogOpenChange = (open: boolean) => {
    setMetadataDialogOpen(open)
    if (!open) {
      setActiveMetadataContext(null)
      setMetadataRows([])
      setMetadataTotals({ orders: 0, revenue: 0 })
      setMetadataCurrency(null)
      setIsMetadataLoading(false)
    }
  }

  const handleMetadataBadgeClick = async (context: MetadataBadgeContext) => {
    const uniqueOrderIds = Array.from(new Set((context.orderIds || []).filter(Boolean)))

    setActiveMetadataContext(context)
    setMetadataDialogOpen(true)
    setMetadataRows([])
    setMetadataTotals({ orders: 0, revenue: 0 })
    setMetadataCurrency(null)

    if (uniqueOrderIds.length === 0) {
      setIsMetadataLoading(false)
      return
    }

    setIsMetadataLoading(true)

    try {
      const response = await fetchWithAuth("/api/integrations/shopify/attribution-stats/metadata", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          integration_id: integrationId,
          order_ids: uniqueOrderIds,
          badge_type: context.badgeType,
          attribution_model: attributionModel,
        }),
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const data = await response.json()
      setMetadataRows(Array.isArray(data.rows) ? data.rows : [])
      setMetadataTotals({
        orders: data.totals?.orders ?? 0,
        revenue: data.totals?.revenue ?? 0,
      })
      setMetadataCurrency(typeof data.currency === "string" && data.currency ? data.currency : null)
    } catch (error) {
      console.error("[Attribution Dashboard] Failed to load metadata details:", error)
      toast({
        variant: "destructive",
        title: tUi("unableToLoadDetails"),
        description: tUi("weCouldNotLoadAdditionalAttributionMetadataPlease"),
      })
    } finally {
      setIsMetadataLoading(false)
    }
  }

  const exportMetadataToExcel = (
    rowsOverride?: MetadataTableRow[],
    sortConfig: MetadataSortConfig | null = null,
  ) => {
    const sourceRows = rowsOverride ?? metadataRows

    if (!sourceRows.length) {
      return
    }

    const rowsForExport = sortConfig
      ? [...sourceRows].sort((a, b) => {
          const { key, direction } = sortConfig
          const modifier = direction === "asc" ? 1 : -1

          switch (key) {
            case "orders":
            case "revenue":
            case "average_order_value": {
              const valueA = a[key] ?? 0
              const valueB = b[key] ?? 0

              if (valueA === valueB) {
                return 0
              }

              return valueA > valueB ? modifier : -modifier
            }
            case "utm_source":
            case "utm_campaign":
            case "utm_medium":
            case "referrer_url": {
              const valueA = (a[key] || "").toLowerCase()
              const valueB = (b[key] || "").toLowerCase()
              return valueA.localeCompare(valueB) * modifier
            }
            case "discount_code": {
              const valueA = (a.discount_code || "").toLowerCase()
              const valueB = (b.discount_code || "").toLowerCase()
              return valueA.localeCompare(valueB) * modifier
            }
            default:
              return 0
          }
        })
      : [...sourceRows]

    const worksheetData = rowsForExport.map((row) => ({
      utm_source: row.utm_source === "—" ? "" : row.utm_source,
      utm_campaign: row.utm_campaign === "—" ? "" : row.utm_campaign,
      utm_medium: row.utm_medium === "—" ? "" : row.utm_medium,
      referrer_url: row.referrer_url === "—" ? "" : row.referrer_url,
      orders: row.orders,
      revenue: Number(row.revenue.toFixed(2)),
      average_order_value: Number(row.average_order_value.toFixed(2)),
      discount_code: row.discount_code ?? "",
    }))

    const worksheet = XLSX.utils.json_to_sheet(worksheetData)
    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 24 },
      { wch: 18 },
      { wch: 40 },
      { wch: 10 },
      { wch: 14 },
      { wch: 16 },
      { wch: 18 },
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Details")

    const endInclusive = new Date(dateRange.month_end_utc.getTime() - 1)
    const filenameParts = [
      activeMetadataContext?.channelName || "channel",
      metadataSubtitle ? metadataSubtitle.replace(/\s+/g, "-") : null,
      activeMetadataContext?.badgeType || "metadata",
      format(dateRange.month_start_utc, "yyyyMMdd"),
      format(endInclusive, "yyyyMMdd"),
    ]
    const filename = filenameParts
      .filter(Boolean)
      .join("_")
      .replace(/[^a-z0-9_-]+/gi, "_")

    XLSX.writeFile(workbook, `${filename || "metadata_details"}.xlsx`)
  }

  const handleMetadataSourceLinked = useCallback(() => {
    void refreshStats()
    void refreshInfluencers()
  }, [refreshStats, refreshInfluencers])

  const handleSubmitInfluencer = async () => {
    const trimmedCode = newInfluencerCode.trim()

    const nextErrors: { general?: string; code?: string; name?: string } = {}

    if (!trimmedCode) {
      nextErrors.code = "Discount code is required"
    }

    if (influencerDialogMode === "create") {
      const trimmedName = newInfluencerName.trim()
      if (!trimmedName) {
        nextErrors.name = "Influencer name is required"
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setInfluencerErrors(nextErrors)
      return
    }

    if (influencerDialogMode === "add-code" && targetInfluencer?.archived_at) {
      setInfluencerErrors({
        general: "Cannot add a code to an archived influencer",
      })
      return
    }

    setInfluencerErrors({})
    setIsAddingInfluencer(true)

    const payload: Record<string, unknown> = {
      integration_id: integrationId,
      code: trimmedCode,
    }

    if (influencerDialogMode === "create") {
      payload.name = newInfluencerName.trim()
    } else if (targetInfluencer) {
      payload.influencer_id = targetInfluencer.id
    }

    try {
      const response = await fetchWithAuth("/api/influencers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        toast({
          title: tUi("success"),
          description:
            influencerDialogMode === "create"
              ? "Influencer created successfully"
              : "Discount code linked to influencer",
        })

        setNewInfluencerCode("")
        setNewInfluencerName("")
        setTargetInfluencer(null)
        setInfluencerDialogOpen(false)

        await refreshInfluencers()
        await refreshStats()
      } else {
        const errorData = await response.json()
        if (errorData.fieldErrors) {
          setInfluencerErrors({
            general: errorData.error,
            ...errorData.fieldErrors,
          })
        } else {
          setInfluencerErrors({
            general: errorData.error || "Failed to save influencer",
          })
        }
      }
    } catch (error) {
      console.error("[Attribution Dashboard] Error saving influencer:", error)
      setInfluencerErrors({
        general: "Failed to save influencer",
      })
    } finally {
      setIsAddingInfluencer(false)
    }
  }

  const handleInfluencerAction = async (
    prompt: {
      scope: "influencer" | "code"
      influencerId: string
      influencerName: string
      code?: string
    },
    action: "archive" | "delete",
  ) => {
    try {
      const query = new URLSearchParams({ integration_id: integrationId, mode: action })
      if (prompt.scope === "code" && prompt.code) {
        query.set("code", prompt.code)
      } else {
        query.set("influencer_id", prompt.influencerId)
      }

      const response = await fetchWithAuth(`/api/influencers?${query.toString()}`, {
        method: "DELETE",
      })

      if (response.ok) {
        const message =
          prompt.scope === "code"
            ? action === "archive"
              ? `Code ${prompt.code} archived`
              : `Code ${prompt.code} deleted`
            : action === "archive"
              ? `Influencer ${prompt.influencerName} archived`
              : `Influencer ${prompt.influencerName} deleted`

        toast({
          title: tUi("success"),
          description: message,
        })
        await refreshInfluencers()
        await refreshStats()
      } else {
        const errorData = await response.json()
        toast({
          title: tUi("error"),
          description: errorData.error || "Failed to update influencer",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("[Attribution Dashboard] Error updating influencer:", error)
      toast({
        title: tUi("error"),
        description: tUi("failedToUpdateInfluencer"),
        variant: "destructive",
      })
    }
  }

  const handleDeleteExpense = async () => {
    if (!deleteConfirmation) return

    const { expenseId } = deleteConfirmation

    try {
      const response = await fetchWithAuth(`/api/integrations/shopify/channel-expenses?id=${expenseId}`, {
        method: "DELETE",
      })

      if (response.ok) {
        // Refresh data to update UI and recompute sums
        await refreshStats()
        setDeleteConfirmation(null)
      } else {
        console.error("[Attribution Dashboard] Failed to delete expense")
      }
    } catch (error) {
      console.error("[Attribution Dashboard] Error deleting expense:", error)
    }
  }

  const openDeleteConfirmation = (channelSlug: string, channelName: string, source: string | undefined) => {
    const { currentMonthKey } = expenseEditingRules
    const expenseId = getExpenseId(channelSlug, source, currentMonthKey)

    if (expenseId) {
      setDeleteConfirmation({
        channelSlug,
        channelName,
        source,
        monthKey: currentMonthKey,
        expenseId,
      })
    }
  }

  const saveExpense = async (channelSlug: string, channelName: string, source?: string) => {
    if (source) {
      const { canEdit, reason } = canEditSourceExpense(channelSlug, source)
      if (!canEdit) {
        console.error("[Attribution Dashboard] Cannot save source expense:", reason)
        return
      }
    } else {
      const { canEdit, reason } = canEditChannelExpense(channelSlug)
      if (!canEdit) {
        console.error("[Attribution Dashboard] Cannot save channel expense:", reason)
        return
      }
    }

    if (!isSingleMonthRange(dateRange.month_start_utc, dateRange.month_end_utc, dateRange.timezone)) {
      console.error("[Attribution Dashboard] Cannot save expense: invalid period")
      return
    }

    const key = source ? `${channelSlug}:${source}` : channelSlug
    const amount = expenseInputs[key] || 0

    const monthKey = expenseEditingRules.currentMonthKey
    const {
      month_start_utc: periodStart,
      month_end_utc: periodEnd,
      month_key: periodMonthKey,
    } = getExpensePeriodForMonth(monthKey)

    const requestBody = {
      user_id: userId,
      integration_id: integrationId,
      channel_name: channelName,
      channel_slug: channelSlug,
      source_name: source || null,
      expense_amount: amount,
      currency: "USD",
      period_start: periodStart,
      period_end: periodEnd,
      period_month: periodMonthKey,
      timezone: dateRange.timezone,
    }

    try {
      const response = await fetchWithAuth("/api/integrations/shopify/channel-expenses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })

      if (response.ok) {
        await refreshStats()
        setEditingExpense(null)
      } else {
        const errorData = await response.json()
        console.error("[v0] saveExpense - Error response:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        })
      }
    } catch (error) {
      console.error("[v0] saveExpense - Exception:", error)
    }
  }

  const enrichedStats = useMemo(() => {
    return stats.map((stat) => {
      const { isMultiMonth, monthKeys, expensesByMonth, currentMonthKey } = expenseEditingRules

      let channelOnlyExpense = 0
      if (isMultiMonth) {
        // Sum channel-level expenses across all months (exclude sources)
        monthKeys.forEach((monthKey) => {
          const monthExpenses = expensesByMonth[monthKey] || []
          const channelExpense = monthExpenses.find((e) => e.channel_slug === stat.channelSlug && !e.source_name)
          if (channelExpense) {
            channelOnlyExpense += channelExpense.expense_amount
          }
        })
      } else {
        // Single month - get channel-level expense only
        const currentMonthExpenses = expensesByMonth[currentMonthKey] || []
        const channelExpense = currentMonthExpenses.find((e) => e.channel_slug === stat.channelSlug && !e.source_name)
        if (channelExpense) {
          channelOnlyExpense = channelExpense.expense_amount
        }
      }

      const enrichedSources = stat.sources.map((source) => {
        const sourceExpenseAmount = getEffectiveSourceExpense(stat.channelSlug, source.sourceName)

        let accumulatedChildExpense = 0
        const enrichedChildren = (source.children || []).map((child) => {
          const childExpenseAmount = getEffectiveSourceExpense(stat.channelSlug, child.sourceName)
          accumulatedChildExpense += childExpenseAmount
        const childRoi =
          childExpenseAmount > 0 ? ((child.revenue - childExpenseAmount) / childExpenseAmount) * 100 : 0
        const childCostPerOrder = child.orders > 0 ? childExpenseAmount / child.orders : 0

        return {
          ...child,
          expense: childExpenseAmount,
          selfExpense: childExpenseAmount,
          roi: childRoi,
          costPerOrder: childCostPerOrder,
          utmMetrics: Array.from(new Set(child.utmMetrics)),
        }
      })

        const combinedExpense = sourceExpenseAmount + accumulatedChildExpense
        const sourceRoi = combinedExpense > 0 ? ((source.revenue - combinedExpense) / combinedExpense) * 100 : 0
        const sourceCostPerOrder = source.orders > 0 ? combinedExpense / source.orders : 0

        return {
          ...source,
          expense: combinedExpense,
          selfExpense: sourceExpenseAmount,
          roi: sourceRoi,
          costPerOrder: sourceCostPerOrder,
          utmMetrics: Array.from(new Set(source.utmMetrics)),
          children: enrichedChildren,
        }
      })

      const totalSourceExpense = enrichedSources.reduce((sum, s) => sum + s.expense, 0)
      const totalExpense = channelOnlyExpense + totalSourceExpense

      const roi = totalExpense > 0 ? ((stat.revenue - totalExpense) / totalExpense) * 100 : 0
      const costPerOrder = stat.orders > 0 ? totalExpense / stat.orders : 0

      return {
        ...stat,
        expense: totalExpense,
        channelExpense: channelOnlyExpense,
        roi,
        costPerOrder,
        sources: enrichedSources,
      }
    })
  }, [stats, expenseEditingRules, getEffectiveSourceExpense])

  const sortedStats = useMemo(() => {
    const sorted = [...enrichedStats].sort((a, b) => {
      let aVal = 0,
        bVal = 0

      switch (sortBy) {
        case "channel":
          return sortOrder === "asc" ? a.channel.localeCompare(b.channel) : b.channel.localeCompare(a.channel)
        case "orders":
          aVal = a.orders
          bVal = b.orders
          break
        case "revenue":
          aVal = a.revenue
          bVal = b.revenue
          break
        case "aov":
          aVal = a.averageOrderValue
          bVal = b.averageOrderValue
          break
        case "roi":
          aVal = a.roi
          bVal = b.roi
          break
      }

      return sortOrder === "asc" ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [enrichedStats, sortBy, sortOrder])

  // Channels that exist only in previous period (dropped to 0 in current)
  const previousOnlyChannels = useMemo(() => {
    if (!previous || !comparisonEnabled) return []
    const currentKeys = new Set(sortedStats.map((s) => s.channelSlug))
    const ghost: ChannelStat[] = []
    for (const [key] of previous.channels) {
      if (!currentKeys.has(key)) {
        // Derive human-readable name from slug: "google-ads" → "Google Ads"
        const label = key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        ghost.push({
          channel: label,
          channelSlug: key,
          channelColor: null,
          channelMeta: null,
          orders: 0,
          revenue: 0,
          averageOrderValue: 0,
          orderIds: [],
          sources: [],
          expense: 0,
          roi: 0,
          costPerOrder: 0,
          channelExpense: 0,
        })
      }
    }
    return ghost
  }, [previous, comparisonEnabled, sortedStats])

  // Merge current + ghost rows, re-apply current sort so ghost rows slot in correctly
  const allStats = useMemo(() => {
    if (previousOnlyChannels.length === 0) return sortedStats
    const merged = [...sortedStats, ...previousOnlyChannels]
    return merged.sort((a, b) => {
      let aVal = 0, bVal = 0
      switch (sortBy) {
        case "channel":
          return sortOrder === "asc" ? a.channel.localeCompare(b.channel) : b.channel.localeCompare(a.channel)
        case "orders": aVal = a.orders; bVal = b.orders; break
        case "revenue": aVal = a.revenue; bVal = b.revenue; break
        case "aov": aVal = a.averageOrderValue; bVal = b.averageOrderValue; break
        case "roi": aVal = a.roi ?? 0; bVal = b.roi ?? 0; break
      }
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal
    })
  }, [sortedStats, previousOnlyChannels, sortBy, sortOrder])

  const chartData = useMemo(() => {
    return allStats.map((stat) => {
      const channelKey = stat.channelSlug
      const prevChannel = previous?.channels.get(channelKey)
      return {
        channel: stat.channel,
        revenue: stat.revenue,
        expense: stat.expense ?? 0,
        orders: stat.orders,
        prevRevenue: prevChannel?.revenue ?? 0,
        prevExpense: prevChannel?.expense ?? 0,
        prevOrders: prevChannel?.orders ?? 0,
      }
    })
  }, [allStats, previous])

  const toggleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortBy(column)
      setSortOrder("desc")
    }
  }

  const totalRevenue = enrichedStats.reduce((sum, stat) => sum + stat.revenue, 0)
  const totalOrders = enrichedStats.reduce((sum, stat) => sum + stat.orders, 0)
  const totalExpense = enrichedStats.reduce((sum, stat) => sum + stat.expense, 0)
  const overallROI = totalExpense > 0 ? ((totalRevenue - totalExpense) / totalExpense) * 100 : 0

  const toggleChannel = (channelKey: string) => {
    const newExpanded = new Set(expandedChannels)
    if (newExpanded.has(channelKey)) {
      newExpanded.delete(channelKey)
      const newFilters = { ...sourceFilters }
      delete newFilters[channelKey]
      setSourceFilters(newFilters)
    } else {
      newExpanded.add(channelKey)
    }
    setExpandedChannels(newExpanded)
  }

  const toggleInfluencerRow = (influencerKey: string) => {
    setExpandedInfluencers((prev) => {
      const next = new Set(prev)
      if (next.has(influencerKey)) {
        next.delete(influencerKey)
      } else {
        next.add(influencerKey)
      }
      return next
    })
  }

  const updateSourceFilter = (channel: string, filterText: string) => {
    const key = channel.toLowerCase()
    setSourceFilters({
      ...sourceFilters,
      [key]: filterText,
    })
  }

  const getFilteredSources = (channel: string, sources: SourceDetail[]) => {
    const filterText = sourceFilters[channel.toLowerCase()]?.toLowerCase() || ""
    if (!filterText) return sources

    return sources.filter((source) => source.sourceName.toLowerCase().includes(filterText))
  }

  const pieChartData = useMemo(() => {
    return sortedStats.map((stat, index) => ({
      channel: stat.channel,
      revenue: stat.revenue,
      percentage: totalRevenue > 0 ? (stat.revenue / totalRevenue) * 100 : 0,
      fill: CHART_COLORS[index % CHART_COLORS.length],
    }))
  }, [sortedStats, totalRevenue])

  const renderCustomLabel = (props: PieLabelRenderProps) => {
    const entry = props as unknown as { channel: string; revenue: number; percentage: number }
    return `${entry.channel}: ${formatCurrency(entry.revenue)} (${entry.percentage.toFixed(1)}%)`
  }

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new()

    const channelsData = sortedStats.map((stat) => ({
      Channel: stat.channel,
      Orders: stat.orders,
      Revenue: stat.revenue,
      "Avg Order Value": stat.averageOrderValue,
      Expense: stat.expense,
      "Cost/Order": stat.costPerOrder,
      "ROI (%)": stat.roi,
    }))

    channelsData.push({
      Channel: "TOTAL",
      Orders: totalOrders,
      Revenue: totalRevenue,
      "Avg Order Value": totalOrders > 0 ? totalRevenue / totalOrders : 0,
      Expense: totalExpense,
      "Cost/Order": totalOrders > 0 ? totalExpense / totalOrders : 0,
      "ROI (%)": overallROI,
    })

    const channelsSheet = XLSX.utils.json_to_sheet(channelsData)
    XLSX.utils.book_append_sheet(workbook, channelsSheet, "Channels")

    sortedStats.forEach((stat) => {
      if (stat.sources.length > 0) {
        const sourcesData = stat.sources.map((source) => ({
          Source: source.sourceName,
          Orders: source.orders,
          Revenue: source.revenue,
          "Avg Order Value": source.averageOrderValue,
          Expense: source.expense,
          "Cost/Order": source.costPerOrder,
          "ROI (%)": source.roi,
          "Actual Code": source.actualCode || "",
          "UTM Metrics": source.utmMetrics && source.utmMetrics.length > 0 ? source.utmMetrics.join("\n\n") : "",
          "Referrer URLs": source.referrerUrls && source.referrerUrls.length > 0 ? source.referrerUrls.join("\n\n") : "",
        }))

        sourcesData.push({
          Source: `${stat.channel} TOTAL`,
          Orders: stat.orders,
          Revenue: stat.revenue,
          "Avg Order Value": stat.averageOrderValue,
          Expense: stat.expense,
          "Cost/Order": stat.costPerOrder,
          "ROI (%)": stat.roi,
          "Actual Code": "",
          "UTM Metrics": "",
          "Referrer URLs": "",
        })

        const sourceSheet = XLSX.utils.json_to_sheet(sourcesData)
        const sheetName = stat.channel.replace(/[:\\/?*[\]]/g, "").substring(0, 31)
        XLSX.utils.book_append_sheet(workbook, sourceSheet, sheetName)
      }
    })

    const notesData = [
      { Note: "Expense = $0.00 means no expense data has been entered for that channel/source." },
      { Note: "ROI when expense is zero is not meaningful — it is shown as 0% in the raw data." },
      { Note: "Cost/Order when expense is zero is $0.00 in the raw data." },
    ]
    const notesSheet = XLSX.utils.json_to_sheet(notesData)
    XLSX.utils.book_append_sheet(workbook, notesSheet, "Notes")

    const rangeEndInclusive = new Date(dateRange.month_end_utc.getTime() - 1)
    const filename = `channel-performance-${format(dateRange.month_start_utc, "yyyy-MM-dd")}-to-${format(
      rangeEndInclusive,
      "yyyy-MM-dd",
    )}.xlsx`

    XLSX.writeFile(workbook, filename)
  }

  // Add delete influencer confirmation dialog
  const [influencerActionPrompt, setInfluencerActionPrompt] = useState<InfluencerActionPrompt | null>(null)

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 rounded-lg border bg-background/95 px-4 pb-5 pt-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <StoreSelect stores={stores} selectedStore={selectedStore} onStoreChange={onStoreChange} />

        <div className="hidden sm:block h-6 w-px bg-border" />

        {/* Data filters group */}
        <PeriodField period={period} customRange={customRange} onPeriodChange={onPeriodChange} dateRange={dateRange} />
        {onComparisonChange && (
          <div className={cn("rounded-md", comparisonMode !== DEFAULT_COMPARISON_MODE && "ring-2 ring-primary/30")}>
            <ComparisonSelector
              mode={comparisonMode}
              customRange={comparisonCustomRange}
              onModeChange={onComparisonChange}
            />
          </div>
        )}
        <div className="hidden sm:block h-6 w-px bg-border" />
        <label className="text-sm font-medium">
          <span><span key={tUi("min")}>{tUi("min")}</span></span><sup title={tUi("minimumOrderTotalOrdersBelowThisAmountAre")} className="cursor-help"><Info className="inline h-2.5 w-2.5 text-muted-foreground" /></sup>
        </label>
        <div className={cn("rounded-md", minimumOrderTotal > 0 && "ring-2 ring-primary/30")}>
          <MinOrderTotalEditor
            currentValue={minimumOrderTotal}
            onSave={onMinOrderSave}
            isLoading={isSettingsLoading}
            canEdit={canManage}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onProductFilterToggle}
          aria-expanded={productFilterOpen}
          aria-controls="product-filter-panel"
          className={cn(productFilterCount > 0 && "ring-2 ring-primary/30")}
        >
          <Package className="mr-2 h-4 w-4" />
          <span><span key={tUi("products")}>{tUi("products")}</span></span>
          {productFilterCount > 0 && (
            <span className="ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {productFilterCount}
            </span>
          )}
        </Button>

        {/* Settings group — separated by wider gap */}
        <div className="ml-auto" />
        <AttributionModelSelect value={attributionModel} onChange={saveAttributionModel} />
        {canManage && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onManageChannels}
          >
            <Settings2 className="mr-2 h-4 w-4" /> <span><span key={tUi("manageChannels")}>{tUi("manageChannels")}</span></span>
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        <span key={tUi("attributionIsRecalculatedAboutEvery10MinutesA")}>{tUi("attributionIsRecalculatedAboutEvery10MinutesA")}</span></p>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {isLoading ? (
          <>
            <KpiCardSkeleton />
            <KpiCardSkeleton />
            <KpiCardSkeleton />
            <KpiCardSkeleton />
          </>
        ) : (
        <>
        <RevenueOrdersKpis
          revenue={totalRevenue}
          orders={totalOrders}
          revenueDelta={deltas?.totals.revenue}
          ordersDelta={deltas?.totals.orders}
          dailySeries={dailySeries}
          ordersHref={buildOrdersPageHref(integrationId, dateRange, productIds)}
        />

        <KpiCard
          label={tUi("totalExpense")}
          icon={TrendingUp}
          formattedValue={totalExpense > 0 ? formatCurrency(totalExpense) : "—"}
          delta={totalExpense > 0 ? deltas?.totals.expense : undefined}
          subtitle={totalExpense === 0 ? "No expense data" : undefined}
        />

        <KpiCard
          label={tUi("overallROI")}
          icon={Percent}
          formattedValue={totalExpense > 0 ? (
            <span className={cn(overallROI >= 0 ? "text-green-600" : "text-red-600")}>
              {overallROI.toFixed(1)}%
            </span>
          ) : "—"}
          delta={totalExpense > 0 ? deltas?.totals.roi : undefined}
          subtitle={totalExpense === 0 ? "No expense data" : undefined}
        />
        </>
        )}
      </div>

      {productIds.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span key={tUi("revenueIsShownOnlyForSelectedProductsLine")}>{tUi("revenueIsShownOnlyForSelectedProductsLine")}</span></AlertDescription>
        </Alert>
      )}

      {!isLoading && sortedStats.length === 0 && (
        <Card className="mx-auto max-w-md text-center">
          <CardContent className="flex flex-col items-center gap-3 py-10">
            <BarChart3 className="h-10 w-10 text-muted-foreground" />
            <CardTitle><span key={tUi("noAttributionData")}>{tUi("noAttributionData")}</span></CardTitle>
            <CardDescription>
              <span key={tUi("noOrdersFoundForTheSelectedPeriodAnd")}>{tUi("noOrdersFoundForTheSelectedPeriodAnd")}</span></CardDescription>
          </CardContent>
        </Card>
      )}

      {(isLoading || sortedStats.length > 0) && <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="table"><span key={tUi("tableView")}>{tUi("tableView")}</span></TabsTrigger>
          <TabsTrigger value="bar-chart"><span key={tUi("barChart")}>{tUi("barChart")}</span></TabsTrigger>
          <TabsTrigger value="line-chart"><span key={tUi("lineChart")}>{tUi("lineChart")}</span></TabsTrigger>
          <TabsTrigger value="pie-chart"><span key={tUi("pieChart")}>{tUi("pieChart")}</span></TabsTrigger>
          {productIds.length > 0 && (
            <TabsTrigger value="product-sales"><span key={tUi("productSales")}>{tUi("productSales")}</span></TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="table" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1.5">
                  <CardTitle><span key={tUi("channelPerformance")}>{tUi("channelPerformance")}</span></CardTitle>
                  <CardDescription><span key={tUi("detailedBreakdownByMarketingChannelWithDrillDown")}>{tUi("detailedBreakdownByMarketingChannelWithDrillDown")}</span></CardDescription>
                </div>
                <Button onClick={exportToExcel} variant="outline" size="sm" disabled={isLoading}>
                  <Download className="mr-2 h-4 w-4" />
                  <span><span key={tUi("export")}>{tUi("export")}</span></span>
                </Button>
              </div>
            </CardHeader>
                        <CardContent>
              <ChannelPerformanceTable
                stats={allStats}
                isLoading={isLoading}
                toggleSort={toggleSort}
                canEditChannelExpense={canEditChannelExpense}
                canEditSourceExpense={canEditSourceExpense}
                canDeleteExpense={canDeleteExpense}
                shouldShowDelete={shouldShowDelete}
                openDeleteConfirmation={openDeleteConfirmation}
                editingExpense={editingExpense}
                setEditingExpense={setEditingExpense}
                expenseInputs={expenseInputs}
                setExpenseInputs={setExpenseInputs}
                saveExpense={saveExpense}
                expenseEditingRules={expenseEditingRules}
                expandedChannels={expandedChannels}
                toggleChannel={toggleChannel}
                sourceFilters={sourceFilters}
                updateSourceFilter={updateSourceFilter}
                getFilteredSources={getFilteredSources}
                expandedInfluencers={expandedInfluencers}
                toggleInfluencerRow={toggleInfluencerRow}
                influencers={influencers}
                openCreateInfluencerDialog={openCreateInfluencerDialog}
                openAddCodeDialog={openAddCodeDialog}
                setInfluencerActionPrompt={setInfluencerActionPrompt}
                onMetadataBadgeClick={handleMetadataBadgeClick}
                channelDeltas={deltas?.channels ?? null}
                sourceDeltas={deltas?.sources ?? null}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bar-chart">
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("revenueVsExpenseByChannel")}>{tUi("revenueVsExpenseByChannel")}</span></CardTitle>
              <CardDescription><span key={tUi("compareRevenueAndMarketingSpend")}>{tUi("compareRevenueAndMarketingSpend")}</span></CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  revenue: {
                    label: tUi("revenue2"),
                    color: "hsl(var(--chart-1))",
                  },
                  expense: {
                    label: tUi("expense"),
                    color: "hsl(var(--chart-2))",
                  },
                  prevRevenue: {
                    label: tUi("prevRevenue"),
                    color: "hsl(var(--chart-1))",
                  },
                  prevExpense: {
                    label: tUi("prevExpense"),
                    color: "hsl(var(--chart-2))",
                  },
                }}
                className="h-[400px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="channel" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="revenue" fill="var(--color-revenue)" name="Revenue" />
                    {comparisonEnabled && <Bar dataKey="prevRevenue" fill="var(--color-prevRevenue)" name="Prev Revenue" opacity={0.35} />}
                    <Bar dataKey="expense" fill="var(--color-expense)" name="Expense" />
                    {comparisonEnabled && <Bar dataKey="prevExpense" fill="var(--color-prevExpense)" name="Prev Expense" opacity={0.35} />}
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="line-chart">
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("ordersByChannel")}>{tUi("ordersByChannel")}</span></CardTitle>
              <CardDescription><span key={tUi("orderVolumeComparison")}>{tUi("orderVolumeComparison")}</span></CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  orders: {
                    label: tUi("orders"),
                    color: "hsl(var(--chart-3))",
                  },
                  prevOrders: {
                    label: tUi("prevOrders"),
                    color: "hsl(var(--chart-3))",
                  },
                }}
                className="h-[400px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="channel" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Line type="monotone" dataKey="orders" stroke="var(--color-orders)" name="Orders" strokeWidth={2} />
                    {comparisonEnabled && <Line type="monotone" dataKey="prevOrders" stroke="var(--color-prevOrders)" name="Prev Orders" strokeWidth={2} strokeDasharray="5 5" opacity={0.5} />}
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pie-chart">
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("revenueDistributionByChannel")}>{tUi("revenueDistributionByChannel")}</span></CardTitle>
              <CardDescription><span key={tUi("percentageBreakdownOfRevenueAcrossChannels")}>{tUi("percentageBreakdownOfRevenueAcrossChannels")}</span></CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : pieChartData.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <span key={tUi("noDataAvailableForTheSelectedPeriod")}>{tUi("noDataAvailableForTheSelectedPeriod")}</span></div>
              ) : (
                <ChartContainer
                  config={Object.fromEntries(
                    sortedStats.map((stat, index) => [
                      stat.channel.toLowerCase().replace(/\s+/g, "-"),
                      {
                        label: stat.channel,
                        color: CHART_COLORS[index % CHART_COLORS.length],
                      },
                    ]),
                  )}
                  className="h-[400px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomLabel}
                        outerRadius={120}
                        dataKey="revenue"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <ChartTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium">{data.channel}</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-muted-foreground"><span key={tUi("revenue")}>{tUi("revenue")}</span></span>
                                    <span className="text-xs font-mono font-medium">
                                      {formatCurrency(data.revenue)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-muted-foreground"><span key={tUi("percentage")}>{tUi("percentage")}</span></span>
                                    <span className="text-xs font-mono font-medium">{data.percentage.toFixed(1)}%</span>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {productIds.length > 0 && (
          <TabsContent value="product-sales">
            <ProductSalesChart
              series={productDailySeries}
              isLoading={productDailyLoading}
              productCount={productIds.length}
            />
          </TabsContent>
        )}
      </Tabs>}

      <InfluencerDialog
        open={influencerDialogOpen}
        mode={influencerDialogMode}
        onOpenChange={handleInfluencerDialogOpenChange}
        onSubmit={handleSubmitInfluencer}
        errors={influencerErrors}
        codeValue={newInfluencerCode}
        onCodeChange={handleInfluencerCodeChange}
        nameValue={newInfluencerName}
        onNameChange={handleInfluencerNameChange}
        isSubmitting={isAddingInfluencer}
        targetInfluencerName={targetInfluencer?.name || ""}
      />

      <MetadataDialog
        open={metadataDialogOpen}
        onOpenChange={handleMetadataDialogOpenChange}
        title={activeMetadataContext?.channelName || "Channel details"}
        subtitle={metadataSubtitle || undefined}
        badgeLabel={metadataBadgeLabel || undefined}
        totals={metadataTotals}
        currency={metadataCurrency}
        isLoading={isMetadataLoading}
        rows={metadataRows}
        onExport={exportMetadataToExcel}
        isExportDisabled={metadataRows.length === 0}
        linkedOrderCount={linkedOrderCount}
        integrationId={integrationId}
        originChannelSlug={activeMetadataContext?.channelSlug ?? null}
        onSourceLinked={handleMetadataSourceLinked}
      />

      <AlertDialog open={!!deleteConfirmation} onOpenChange={(open) => !open && setDeleteConfirmation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("deleteExpense")}>{tUi("deleteExpense")}</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span><span key={tUi("deleteExpenseFor")}>{tUi("deleteExpenseFor")}</span></span>{" "}
              <span key={deleteConfirmation?.source
                ? tUi("value0Value1", { value0: String(deleteConfirmation.channelName), value1: String(deleteConfirmation.source) })
                : deleteConfirmation?.channelName}>{deleteConfirmation?.source
                ? tUi("value0Value1", { value0: String(deleteConfirmation.channelName), value1: String(deleteConfirmation.source) })
                : deleteConfirmation?.channelName}</span>{" "}
              <span>—</span> {deleteConfirmation?.monthKey}<span>?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExpense}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span key={tUi("delete")}>{tUi("delete")}</span></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!influencerActionPrompt}
        onOpenChange={(open) => !open && setInfluencerActionPrompt(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <span key={influencerActionPrompt?.scope === "influencer"
                ? influencerActionPrompt?.isInfluencerArchived
                  ? tUi("archivedInfluencerOptions")
                  : tUi("archiveOrDeleteInfluencer")
                : influencerActionPrompt?.isCodeArchived
                  ? tUi("archivedCodeOptions")
                  : tUi("archiveOrDeleteCode")}>{influencerActionPrompt?.scope === "influencer"
                ? influencerActionPrompt?.isInfluencerArchived
                  ? tUi("archivedInfluencerOptions")
                  : tUi("archiveOrDeleteInfluencer")
                : influencerActionPrompt?.isCodeArchived
                  ? tUi("archivedCodeOptions")
                  : tUi("archiveOrDeleteCode")}</span>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {influencerActionPrompt?.scope === "influencer" ? (
                    <>
                      <div>
                        <span className="font-semibold"><span key={tUi("archive")}>{tUi("archive")}</span></span> <span><span key={tUi("keepsHistoricalAttributionIntactButStopsFutureOrders")}>{tUi("keepsHistoricalAttributionIntactButStopsFutureOrders")}</span></span>{" "}
                        <span className="font-semibold">{influencerActionPrompt.influencerName}</span><span>.</span>
                      </div>
                      <div>
                        <span className="font-semibold text-destructive"><span key={tUi("delete")}>{tUi("delete")}</span></span> <span><span key={tUi("removesTheInfluencerAndAllRelatedCodesPast")}>{tUi("removesTheInfluencerAndAllRelatedCodesPast")}</span></span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="font-semibold"><span key={tUi("archive")}>{tUi("archive")}</span></span> <span><span key={tUi("preservesReportingHistoryForCode")}>{tUi("preservesReportingHistoryForCode")}</span></span>{" "}
                        <span className="font-semibold">{influencerActionPrompt?.code}</span> <span><span key={tUi("butBlocksItFromMatchingFutureOrders")}>{tUi("butBlocksItFromMatchingFutureOrders")}</span></span>
                      </div>
                      <div>
                        <span className="font-semibold text-destructive"><span key={tUi("delete")}>{tUi("delete")}</span></span> <span><span key={tUi("permanentlyRemovesTheCodePastOrdersWillBe")}>{tUi("permanentlyRemovesTheCodePastOrdersWillBe")}</span></span>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-3 text-xs font-semibold uppercase text-destructive">
                  <span key={tUi("theseActionsAreIrreversible")}>{tUi("theseActionsAreIrreversible")}</span></div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
              <Button
                variant="secondary"
                disabled={
                  (influencerActionPrompt?.scope === "influencer" &&
                    influencerActionPrompt?.isInfluencerArchived) ||
                  (influencerActionPrompt?.scope === "code" && influencerActionPrompt?.isCodeArchived)
                }
                onClick={() => {
                  if (influencerActionPrompt) {
                    void handleInfluencerAction(influencerActionPrompt, "archive")
                    setInfluencerActionPrompt(null)
                  }
                }}
              >
                <span key={tUi("archive")}>{tUi("archive")}</span></Button>
              <AlertDialogAction
                onClick={() => {
                  if (influencerActionPrompt) {
                    void handleInfluencerAction(influencerActionPrompt, "delete")
                    setInfluencerActionPrompt(null)
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <span key={tUi("delete")}>{tUi("delete")}</span></AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
